import React, { useEffect } from "react";
import { Box, Typography, Avatar, List, ListItem, Stack, Chip, Select, MenuItem, IconButton, InputAdornment, TextField, } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos, Clear } from "@mui/icons-material";
import { FaSearch } from "react-icons/fa";
import { t } from "i18next";
import { FilterItem } from "../../components/Modals/CertConnectCustomFilterModal";
import "../../sass/base/listView.scss";
import iconImage from "../../assets/images/user-image.png";

interface Props {
  gridRows: any[];
  handleSearch: () => void;
  handleClick: (row: any) => void;
  totalCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: React.Dispatch<React.SetStateAction<{ page: number; pageSize: number }>>;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  onSearch: () => void;
  onRowClick: (row: any) => void;
  filters?: FilterItem[];
}

const ListViewNeedAttention: React.FC<Props> = ({
  gridRows,
  handleSearch,
  handleClick,
  totalCount,
  paginationModel,
  setPaginationModel,
  searchTerm,
  setSearchTerm,
}) => {
  const ACTION_REQUIRED_LABELS: Record<number, string> = {
    1: t("dataGrid.accountLocked"),
    2: t("dataGrid.pendingConnectionApproval"),
    3: t("dataGrid.noCertifications"),
    4: t("dataGrid.missingPhoto"),
  };
  const ACTION_REQUIRED_CODE_BY_LABEL: Record<string, number> = {
    "Account Locked": 1,
    "Pending Connection Approval": 2,
    "No Certifications": 3,
    "Missing Photo": 4,
  };
  const getActionRequiredValue = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return null;

      const mapped = ACTION_REQUIRED_CODE_BY_LABEL[s];
      if (mapped) return mapped;

      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }

    const n = Number(v as any);
    return Number.isFinite(n) ? n : null;
  };
  useEffect(() => {
    if (searchTerm === "") {
      handleSearch();
    }
  }, [searchTerm]);
  const getActionRequired = (actionRequired: number | null) => {
    if (actionRequired === null || actionRequired === undefined) return null;

    const label = ACTION_REQUIRED_LABELS[actionRequired] ?? String(actionRequired);

    if (actionRequired === 1) {
      return { label: t("dataGrid.accountLocked"), color: "#fff", backgroundColor: "#ff0000" };
    } else if (actionRequired === 2) {
      return { label: t("dataGrid.pendingConnectionApproval"), color: "#000000", backgroundColor: "#ffff00" };
    } else if (actionRequired === 3) {
      return { label: t("dataGrid.noCertifications"), color: "#fff", backgroundColor: "#9e9e9e" };
    } else if (actionRequired === 4) {
      return { label: t("dataGrid.missingPhoto"), color: "#fff", backgroundColor: "#ff0000" };
    }

    return { label, color: "#000", backgroundColor: "#eee" };
  };
  return (
    <Box className="main-box">
      <Typography className="typography">
        {t("dataGrid.ResultsCount", { count: totalCount })}
      </Typography>

      <Box className="inner-box">
        <TextField
          size="small"
          placeholder={t("dataGrid.SearcboxNeed")}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
          className="text-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box className="input-box">
                  {searchTerm && (
                    <Clear
                      fontSize="small"
                      onClick={() => { setSearchTerm("");}}
                      className="clear-icon"
                    />
                  )}
                  <FaSearch
                    className="search-icon clickable"
                    onClick={handleSearch}
                  />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <List sx={{ p: 0 }}>
        {gridRows.map((row) => {
          const action = getActionRequired(getActionRequiredValue((row as any).ActionRequired));

          return (
            <ListItem
              key={row.uniqueId}
              onClick={() => handleClick(row)}
              className="list-item"
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={2}>
                  <div className="certconnect-list-photo">
                    <img src={row.ContactProfilePic || iconImage} alt="profile" />
                  </div>
                  <Box>
                    <Typography fontWeight={600} color="#171725">
                      {row.ContactName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CCO ID: {row.ContactCCOID || "—"}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>

                      {action && (
                        <Chip label={action.label} size="small"
                          sx={{
                            backgroundColor: action.backgroundColor, color: action.color,
                            fontWeight: 600, height: '20px', fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
      {totalCount > 0 && (
        <Stack spacing={1} alignItems="left" sx={{ mt: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            {/* Rows per page */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography >
                {t("dataGrid.Rows")}
              </Typography>
              <Select
                size="small"

                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                    page: 0,
                  }))
                }
                sx={{
                  minWidth: 40,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "none" },
                  "& .MuiSelect-select": { py: 0.5, px: 1, fontSize: "0.8rem" },
                }}
              >
                {[10, 25, 50].map((size) => (
                  <MenuItem key={size} value={size} sx={{ fontSize: "0.7rem" }}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Current range / total */}
            <Typography variant="body2" fontWeight={600}>
              {paginationModel.page * paginationModel.pageSize + 1} –{" "}
              {Math.min(
                (paginationModel.page + 1) * paginationModel.pageSize,
                totalCount
              )}{" "}
              of {totalCount}
            </Typography>

            {/* Previous / Next arrows */}
            <IconButton
              size="small"
              disabled={paginationModel.page === 0}
              onClick={() =>
                setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))
              }
              sx={{ color: "#363534" }}
            >
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={
                (paginationModel.page + 1) * paginationModel.pageSize >= totalCount
              }
              onClick={() =>
                setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              sx={{ color: "#363534" }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default ListViewNeedAttention;
